import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, isToday, isSameMonth, addDays, isAfter } from 'date-fns';
import { fr } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Paper, Box, Typography, CircularProgress, Button,
  IconButton, Chip, Tooltip, useTheme, Skeleton, Divider
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, CalendarToday,
  Event, Assignment, Rocket, OpenInNew, Circle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { calendarService } from '../services/api';

// ── Localizer ──────────────────────────────────────────────────────
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (d) => startOfWeek(d, { locale: fr }),
  getDay,
  locales: { fr },
});

const RBC_MESSAGES = {
  allDay: 'Journée', previous: '‹', next: '›', today: "Aujourd'hui",
  month: 'Mois', week: 'Semaine', day: 'Jour', agenda: 'Agenda',
  date: 'Date', time: 'Heure', event: 'Événement',
  noEventsInRange: 'Aucun événement sur cette période.',
  showMore: (n) => `+${n}`,
};

const TYPE_CONFIG = {
  meeting: { color: '#9c27b0', light: '#f3e5f5', Icon: Event,      label: 'Réunion'  },
  task:    { color: '#1976d2', light: '#e3f2fd', Icon: Assignment,  label: 'Tâche'    },
  project: { color: '#2e7d32', light: '#e8f5e9', Icon: Rocket,      label: 'Projet'   },
};

// ── Custom Toolbar ─────────────────────────────────────────────────
function CustomToolbar({ label, onNavigate, onView, view }) {
  const theme = useTheme();
  const views = [
    { key: 'month',  label: 'Mois'    },
    { key: 'week',   label: 'Semaine' },
    { key: 'day',    label: 'Jour'    },
    { key: 'agenda', label: 'Agenda'  },
  ];

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      mb: 1.5, flexWrap: 'wrap', gap: 1,
    }}>
      {/* Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size="small" onClick={() => onNavigate('PREV')}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5 }}>
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Button
          size="small"
          onClick={() => onNavigate('TODAY')}
          startIcon={<CalendarToday sx={{ fontSize: '14px !important' }} />}
          sx={{
            borderRadius: 1.5, textTransform: 'none', fontWeight: 600,
            fontSize: '0.8rem', px: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            color: 'text.primary',
          }}
        >
          Auj.
        </Button>
        <IconButton size="small" onClick={() => onNavigate('NEXT')}
          sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5 }}>
          <ChevronRight fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight="700" sx={{ ml: 1, minWidth: 140 }}>
          {label}
        </Typography>
      </Box>

      {/* View selector */}
      <Box sx={{
        display: 'flex', gap: 0.5, bgcolor: 'action.hover',
        borderRadius: 2, p: 0.5,
      }}>
        {views.map(v => (
          <Button
            key={v.key}
            size="small"
            onClick={() => onView(v.key)}
            sx={{
              borderRadius: 1.5, textTransform: 'none', fontWeight: 600,
              fontSize: '0.75rem', px: 1.5, py: 0.4, minWidth: 0,
              bgcolor: view === v.key ? 'background.paper' : 'transparent',
              color: view === v.key ? 'primary.main' : 'text.secondary',
              boxShadow: view === v.key ? 1 : 'none',
              '&:hover': { bgcolor: view === v.key ? 'background.paper' : 'action.selected' },
            }}
          >
            {v.label}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

// ── Custom Event pill ──────────────────────────────────────────────
function CustomEvent({ event }) {
  const cfg = TYPE_CONFIG[event.type] || TYPE_CONFIG.task;
  const cleanTitle = event.title.replace(/^[📅✓🚀]\s*/, '');
  return (
    <Tooltip title={`${cfg.label} · ${cleanTitle}`} placement="top" arrow>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.4,
        px: 0.6, py: 0.1, borderRadius: '3px',
        bgcolor: event.color, color: '#fff',
        overflow: 'hidden', height: '100%',
        fontSize: '0.72rem', fontWeight: 600,
        lineHeight: 1.2,
      }}>
        <cfg.Icon sx={{ fontSize: '10px', flexShrink: 0, opacity: 0.9 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cleanTitle}
        </span>
      </Box>
    </Tooltip>
  );
}

// ── CSS overrides injected once ────────────────────────────────────
const CALENDAR_OVERRIDES_ID = 'era-calendar-widget-css';
function injectCalendarCSS(isDark) {
  let el = document.getElementById(CALENDAR_OVERRIDES_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = CALENDAR_OVERRIDES_ID;
    document.head.appendChild(el);
  }
  const bg        = isDark ? '#1e293b' : '#ffffff';
  const bgAlt     = isDark ? '#0f172a' : '#f8fafc';
  const border    = isDark ? '#334155' : '#e2e8f0';
  const text      = isDark ? '#e2e8f0' : '#1e293b';
  const textMuted = isDark ? '#94a3b8' : '#64748b';
  const todayBg   = isDark ? '#1e3a5f' : '#eff6ff';
  const headerBg  = isDark ? '#0f172a' : '#f1f5f9';

  el.textContent = `
    .rbc-calendar { font-family: inherit; color: ${text}; }
    .rbc-header { background: ${headerBg}; border-color: ${border} !important; padding: 6px 4px; font-size: 0.75rem; font-weight: 700; color: ${textMuted}; letter-spacing: 0.04em; text-transform: uppercase; }
    .rbc-month-view, .rbc-time-view, .rbc-agenda-view { border-color: ${border} !important; border-radius: 8px; overflow: hidden; }
    .rbc-day-bg { background: ${bg}; }
    .rbc-day-bg + .rbc-day-bg { border-color: ${border} !important; }
    .rbc-month-row { border-color: ${border} !important; }
    .rbc-month-row + .rbc-month-row { border-color: ${border} !important; }
    .rbc-date-cell { padding: 4px 6px 2px; font-size: 0.78rem; color: ${textMuted}; }
    .rbc-date-cell.rbc-now > button { background: #2196F3; color: #fff; border-radius: 50%; width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.78rem; }
    .rbc-today { background-color: ${todayBg} !important; }
    .rbc-off-range-bg { background: ${bgAlt}; }
    .rbc-off-range .rbc-date-cell { color: ${border}; }
    .rbc-toolbar { display: none; }
    .rbc-show-more { color: #2196F3; font-size: 0.72rem; font-weight: 700; padding: 0 4px; background: transparent; }
    .rbc-event { border-radius: 3px !important; border: none !important; padding: 0 !important; }
    .rbc-event:focus { outline: none; box-shadow: 0 0 0 2px #2196F380; }
    .rbc-event-label { display: none; }
    .rbc-slot-selection { background: #2196F330; }
    .rbc-time-header { background: ${headerBg}; border-color: ${border} !important; }
    .rbc-time-content { border-color: ${border} !important; }
    .rbc-time-slot { border-color: ${border}50 !important; color: ${textMuted}; font-size: 0.7rem; }
    .rbc-timeslot-group { border-color: ${border} !important; }
    .rbc-current-time-indicator { background: #2196F3; height: 2px; }
    .rbc-agenda-table { border-color: ${border} !important; }
    .rbc-agenda-table thead { background: ${headerBg}; color: ${textMuted}; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; }
    .rbc-agenda-date-cell, .rbc-agenda-time-cell { color: ${textMuted}; font-size: 0.78rem; padding: 6px 10px; border-color: ${border} !important; }
    .rbc-agenda-event-cell { padding: 6px 10px; border-color: ${border} !important; font-size: 0.82rem; color: ${text}; }
    .rbc-agenda-empty { color: ${textMuted}; font-size: 0.85rem; padding: 20px; text-align: center; }
    .rbc-row-segment .rbc-event-content { font-size: 0.72rem; }
  `;
}

// ── Main Component ─────────────────────────────────────────────────
const CalendarWidget = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('month');
  const [date, setDate]       = useState(new Date());

  // Inject CSS overrides whenever dark mode changes
  useEffect(() => { injectCalendarCSS(isDark); }, [isDark]);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await calendarService.getEvents();
      setEvents(response.data.map(e => ({
        ...e,
        start: new Date(e.start),
        end:   new Date(e.end),
      })));
    } catch {
      // silent fail in widget context
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // ── Stats ────────────────────────────────────────────────────
  const now = new Date();
  const todayEvents    = events.filter(e => isToday(e.start));
  const thisMonthEvts  = events.filter(e => isSameMonth(e.start, date));
  const upcomingEvents = events
    .filter(e => isAfter(e.start, now) && isAfter(addDays(now, 7), e.start))
    .sort((a, b) => a.start - b.start)
    .slice(0, 3);

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      borderRadius: '3px',
      color: 'white',
      border: 'none',
      padding: 0,
    },
  });

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        px: 3, py: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            bgcolor: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(33,150,243,0.35)',
          }}>
            <CalendarToday sx={{ color: '#fff', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="800" sx={{ lineHeight: 1.2, letterSpacing: '-0.3px' }}>
              Mon Agenda
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {loading ? '…' : `${thisMonthEvts.length} événement${thisMonthEvts.length !== 1 ? 's' : ''} ce mois`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Type chips */}
          {!loading && Object.entries(TYPE_CONFIG).map(([type, cfg]) => {
            const count = events.filter(e => e.type === type).length;
            if (count === 0) return null;
            return (
              <Tooltip key={type} title={`${count} ${cfg.label}${count > 1 ? 's' : ''}`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Circle sx={{ fontSize: 8, color: cfg.color }} />
                  <Typography variant="caption" fontWeight="600" sx={{ color: cfg.color }}>{count}</Typography>
                </Box>
              </Tooltip>
            );
          })}

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Button
            size="small"
            endIcon={<OpenInNew sx={{ fontSize: '13px !important' }} />}
            onClick={() => navigate('/calendar')}
            sx={{
              borderRadius: 2, textTransform: 'none',
              fontWeight: 600, fontSize: '0.78rem',
              border: `1px solid ${theme.palette.divider}`,
              color: 'text.primary', px: 1.5,
              '&:hover': { bgcolor: 'primary.main', color: '#fff', borderColor: 'primary.main' },
              transition: 'all 0.2s',
            }}
          >
            Complet
          </Button>
        </Box>
      </Box>

      {/* ── Today's events strip ── */}
      {!loading && todayEvents.length > 0 && (
        <Box sx={{
          px: 3, py: 1,
          bgcolor: theme.palette.mode === 'dark' ? '#1e3a5f30' : '#eff6ff',
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        }}>
          <Typography variant="caption" fontWeight="700" color="primary.main" sx={{ mr: 0.5 }}>
            AUJOURD'HUI ·
          </Typography>
          {todayEvents.slice(0, 4).map(e => {
            const cfg = TYPE_CONFIG[e.type] || TYPE_CONFIG.task;
            return (
              <Chip
                key={e.id}
                icon={<cfg.Icon sx={{ fontSize: '12px !important', color: `${cfg.color} !important` }} />}
                label={e.title.replace(/^[📅✓🚀]\s*/, '').substring(0, 28)}
                size="small"
                sx={{
                  fontSize: '0.7rem', fontWeight: 600, height: 22,
                  bgcolor: cfg.color + '15', color: cfg.color,
                  border: `1px solid ${cfg.color}30`,
                  '& .MuiChip-icon': { ml: '6px' },
                }}
              />
            );
          })}
          {todayEvents.length > 4 && (
            <Typography variant="caption" color="text.secondary">+{todayEvents.length - 4}</Typography>
          )}
        </Box>
      )}

      {/* ── Calendar body ── */}
      <Box sx={{ p: 2 }}>
        {loading ? (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Skeleton variant="rounded" width={200} height={32} />
              <Skeleton variant="rounded" width={220} height={32} />
            </Box>
            <Skeleton variant="rounded" width="100%" height={420} sx={{ borderRadius: 2 }} />
          </Box>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 480 }}
            messages={RBC_MESSAGES}
            culture="fr"
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'day', 'agenda']}
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            components={{
              toolbar: CustomToolbar,
              event:   CustomEvent,
            }}
            popup
            tooltipAccessor={null}
            onSelectEvent={(event) => {
              if (event.type === 'task' && event.details?.project_id) {
                navigate(`/projects/${event.details.project_id}`);
              } else if (event.type === 'project' && event.details?.id) {
                navigate(`/projects/${event.details.id}`);
              } else if (event.details?.meeting_link) {
                window.open(event.details.meeting_link, '_blank');
              } else {
                navigate('/calendar');
              }
            }}
          />
        )}
      </Box>

      {/* ── Upcoming events footer ── */}
      {!loading && upcomingEvents.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ letterSpacing: '0.05em' }}>
              À VENIR
            </Typography>
            {upcomingEvents.map(e => {
              const cfg = TYPE_CONFIG[e.type] || TYPE_CONFIG.task;
              return (
                <Box
                  key={e.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.6, cursor: 'pointer' }}
                  onClick={() => navigate('/calendar')}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cfg.color, flexShrink: 0 }} />
                  <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>
                    {e.title.replace(/^[📅✓🚀]\s*/, '')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    · {format(e.start, 'dd MMM', { locale: fr })}
                  </Typography>
                </Box>
              );
            })}
            <Button
              size="small"
              onClick={() => navigate('/calendar')}
              sx={{ ml: 'auto', fontSize: '0.72rem', textTransform: 'none', color: 'primary.main', fontWeight: 700 }}
            >
              Voir tout →
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default CalendarWidget;
